import { useContext, useEffect, useMemo, useState } from "react";
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Divider,
  Pagination,
  Paper,
  Stack,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import {
  DataGridPro,
  jaJP,
  type GridColDef,
  type GridSortModel,
} from "@mui/x-data-grid-pro";
import type { ICommercialFeedItem } from "../types";
import {
  RECEPTION_BOOK_PAGE_SIZE,
  useCorporateReceptionBooks,
} from "../hooks/useCorporateReceptionBooks";
import { toReceptionBookOrdering } from "../utils/receptionBookOrdering";
import { useContactFormDialog } from "@/hooks/useContactFormDialog";
import { userDataContext } from "@/hooks/useUserData";

export const ReceptionBooksTab: React.FC<{ corporateNumber: string }> = ({
  corporateNumber,
}) => {
  // 定額プラン(flat_monthly)ユーザーのみ受付帳を全件閲覧できる。
  // それ以外はペイウォール表示（従量課金/未加入）。
  // App の userData（userMe 取得後に更新される）を参照する。useUserData() は別インスタンスになり、
  // プラン変更後も初期値のまま有料プラン案内が出続けるため使わない（既存の受付帳と同じ）。
  const { data: userData } = useContext(userDataContext);
  const hasFullAccess =
    userData.commercialReceptionBookViewPlan === "flat_monthly";
  const { openContactForm } = useContactFormDialog();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  // 受付帳は件数が多い法人もあるため、BE でページ取得する（0始まり）。
  const [page, setPage] = useState(0);
  // 並び替えも BE で全件に対して行う（表示中のページの中だけを並べ替えると、全件の順に見えて誤解を招くため）。
  const [sortModel, setSortModel] = useState<GridSortModel>([
    { field: "legalAffairsBureauRequestDate", sort: "desc" },
  ]);
  useEffect(() => {
    setPage(0);
  }, [corporateNumber]);
  const { data, isLoading, error, mutate } = useCorporateReceptionBooks(
    corporateNumber,
    hasFullAccess,
    page,
    toReceptionBookOrdering(sortModel)
  );
  const pageCount = Math.ceil((data?.count ?? 0) / RECEPTION_BOOK_PAGE_SIZE);
  const columns = useMemo<Array<GridColDef<ICommercialFeedItem>>>(
    () => [
      {
        field: "legalAffairsBureauRequestDate",
        headerName: "受付日",
        width: 120,
      },
      {
        field: "legalAffairsBureauReceptionNumber",
        headerName: "受付番号",
        width: 110,
      },
      {
        field: "receptionReason",
        headerName: "登記事由",
        width: 180,
        valueGetter: ({ row }) =>
          row.receptionReasons.join(" / ") || (row.receptionReason ?? "—"),
        // 複数の登記事由をつないだ表示のため、並び替えの対象にしない
        sortable: false,
      },
      { field: "commercialRequesterTypeName", headerName: "種別", width: 120 },
    ],
    []
  );
  return (
    <Stack spacing={2}>
      {!hasFullAccess ? (
        <Stack spacing={2}>
          <Alert severity="info">
            商業登記受付帳は有料プランでご利用いただけます
          </Alert>
          <Button
            variant="outlined"
            sx={{ alignSelf: "flex-start" }}
            onClick={openContactForm}
          >
            有料プランのご案内
          </Button>
        </Stack>
      ) : isLoading ? (
        <CircularProgress aria-label="受付帳を読み込み中" />
      ) : error ? (
        <Alert
          severity="error"
          action={
            <Button
              onClick={() => {
                void mutate();
              }}
            >
              再試行
            </Button>
          }
        >
          受付帳の取得に失敗しました。
        </Alert>
      ) : (
        <>
          <Typography color="text.secondary">
            会社法人等番号：{corporateNumber.slice(1)} ／ {data?.count ?? 0}件
          </Typography>
          {isMobile ? (
            (data?.list ?? []).length === 0 ? (
              <Alert severity="info">受付帳履歴がありません</Alert>
            ) : (
              <Stack spacing={1.5}>
                {(data?.list ?? []).map((item, i) => {
                  const reason =
                    item.receptionReasons.join(" / ") ||
                    (item.receptionReason ?? "—");
                  return (
                    <Paper key={i} variant="outlined" sx={{ p: 2 }}>
                      <Stack spacing={1} divider={<Divider flexItem />}>
                        {(
                          [
                            ["受付日", item.legalAffairsBureauRequestDate],
                            [
                              "受付番号",
                              item.legalAffairsBureauReceptionNumber,
                            ],
                            ["登記事由", reason],
                            ["種別", item.commercialRequesterTypeName],
                          ] as const
                        ).map(([label, value]) => (
                          <Stack
                            key={label}
                            direction="row"
                            justifyContent="space-between"
                            spacing={2}
                          >
                            <Typography
                              variant="body2"
                              color="text.secondary"
                              sx={{ flexShrink: 0 }}
                            >
                              {label}
                            </Typography>
                            <Box
                              component="span"
                              sx={{
                                textAlign: "right",
                                overflowWrap: "anywhere",
                                fontSize: "0.875rem",
                              }}
                            >
                              {value ?? "—"}
                            </Box>
                          </Stack>
                        ))}
                      </Stack>
                    </Paper>
                  );
                })}
                {pageCount > 1 ? (
                  <Pagination
                    count={pageCount}
                    page={page + 1}
                    onChange={(_, value) => {
                      setPage(value - 1);
                    }}
                    sx={{ alignSelf: "center" }}
                  />
                ) : null}
              </Stack>
            )
          ) : (
            <DataGridPro
              autoHeight
              rows={data?.list ?? []}
              columns={columns}
              disableSelectionOnClick
              pagination
              paginationMode="server"
              rowCount={data?.count ?? 0}
              page={page}
              onPageChange={setPage}
              pageSize={RECEPTION_BOOK_PAGE_SIZE}
              rowsPerPageOptions={[RECEPTION_BOOK_PAGE_SIZE]}
              sortingMode="server"
              // BE は1項目でしか並べないため、Ctrl/Shift クリックの複数列の並び替えは無効にする
              disableMultipleColumnsSorting
              sortingOrder={["desc", "asc"]}
              sortModel={sortModel}
              onSortModelChange={(model) => {
                setSortModel(model);
                setPage(0);
              }}
              disableColumnMenu
              localeText={{
                ...jaJP.components.MuiDataGrid.defaultProps.localeText,
                noRowsLabel: "受付帳履歴がありません",
              }}
            />
          )}
        </>
      )}
    </Stack>
  );
};
